import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { Button, Form, Input, Message, Link, Typography } from "@arco-design/web-react";
import axios from "axios";

import { endpoints } from "../api/endpoints";
import { resetCachedToken } from "../setupAxios";
import {
  AuthSplitLayout,
  AuthHeadline,
  AuthSubtext,
  AuthFormSection,
} from "../components/auth/AuthSplitLayout";
import { AuthSsoRow } from "../components/auth/AuthSsoRow";
import { BookMeetingAuthModal } from "../components/BookMeetingAuthModal/BookMeetingAuthModal";
import { isQrRegistrationSource, QR_REGISTRATION_SOURCE } from "../constants/qrRegistration";
import { isBookMeetingIntent, registerPathWithBookMeetingIntent } from "../constants/authBookMeeting";
import { registrationAttributionFromSearchParams } from "../constants/guestIntake";
import { routes } from "../routes/routes";
import { RootState } from "../store/store";
import { clearUser, setUser } from "../store/userSlice";
import { completeAuthRedirect, shouldDeferAutoAuthRedirect } from "../utils/authReturnUrl";
import { defaultAuthenticatedPath } from "../utils/defaultAuthenticatedPath";
import { useEventIntakeEnabled } from "../hooks/useEventIntakeEnabled";
import "../components/auth/AuthSplitLayout.css";

const FormItem = Form.Item;

export const LoginPreview = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user.user);
  const qrSignupEnabled = useSelector((state: RootState) => state.config.qrSignupEnabled);
  const brandName = useSelector((state: RootState) => state.config.brandName);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const eventIntakeEnabled = useEventIntakeEnabled();
  const configLoaded = useSelector((state: RootState) => state.config.configLoaded);
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const registrationSource = searchParams.get("source");
  const intent = searchParams.get("intent");
  const isBookMeetingFlow = isBookMeetingIntent(intent);
  const registerHref = isQrRegistrationSource(registrationSource)
    ? `${routes.REGISTER}?source=${QR_REGISTRATION_SOURCE}`
    : isBookMeetingFlow
      ? registerPathWithBookMeetingIntent()
      : routes.REGISTER;
  const [bookMeetingModalOpen, setBookMeetingModalOpen] = useState(isBookMeetingFlow);

  useEffect(() => {
    if (user === null || shouldDeferAutoAuthRedirect() || !configLoaded) {
      return;
    }
    completeAuthRedirect(defaultAuthenticatedPath(user, eventIntakeEnabled, searchParams));
  }, [user, eventIntakeEnabled, searchParams, configLoaded]);

  if (isQrRegistrationSource(registrationSource) && !qrSignupEnabled) {
    return (
      <AuthSplitLayout>
        <AuthHeadline>Event registration unavailable</AuthHeadline>
        <AuthSubtext>
          <Typography.Text>
            QR registration is not enabled for this organization.
          </Typography.Text>
        </AuthSubtext>
      </AuthSplitLayout>
    );
  }

  const onSubmit = async () => {
    if (!form) return;
    form.validate().then(async () => {
      const email = form.getFieldValue("email");
      const password = form.getFieldValue("password");
      const auth = getAuth();
      try {
        resetCachedToken();
        dispatch(clearUser());

        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const firebaseUser = userCredential.user;
        const idToken = await firebaseUser.getIdToken();
        const response = await axios.get(endpoints.whoami, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        dispatch(setUser({ ...response.data }));
        completeAuthRedirect(routes.DASHBOARD);
      } catch (error) {
        console.error("Login failed:", error);
        Message.error(t("notification_messages.logged_in_error"));
        localStorage.removeItem("user");
      }
    });
  };

  return (
    <AuthSplitLayout>
      <BookMeetingAuthModal
        visible={isBookMeetingFlow && bookMeetingModalOpen}
        brandName={brandName || "WERULE"}
        onSignIn={() => setBookMeetingModalOpen(false)}
        onRegister={() => {
          setBookMeetingModalOpen(false);
          navigate(registerHref);
        }}
        onClose={() => setBookMeetingModalOpen(false)}
      />

      <AuthHeadline>{t("login_page.unlock_headline")}</AuthHeadline>
      <AuthSubtext>
        {t("login_page.welcome_subtext")}
        <Link href="https://we-rule.com/terms-conditions" target="_blank">
          {t("register_page.terms")}
        </Link>
      </AuthSubtext>

      <AuthFormSection>
        <Form onSubmit={onSubmit} form={form} layout="vertical" requiredSymbol={false}>
          <FormItem
            className="auth-underline-field"
            field="email"
            label={t("login_page.email_label")}
            rules={[
              { required: true, message: t("login_page.email_placeholder") },
              {
                validator: (v, cb) => {
                  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                  if (!v || v.trim().length === 0) {
                    cb(t("form_validation_messages.empty_email"));
                  } else if (v.length > 100) {
                    cb(t("form_validation_messages.long_email"));
                  } else if (!emailRegex.test(v)) {
                    cb(t("form_validation_messages.invalid_email"));
                  } else {
                    cb();
                  }
                },
              },
            ]}
          >
            <Input placeholder="you@email.com" />
          </FormItem>

          <FormItem
            className="auth-underline-field"
            field="password"
            label={t("login_page.password_label")}
            rules={[
              { required: true, message: t("form_validation_messages.no_password") },
              {
                minLength: 6,
                message: t("form_validation_messages.short_password"),
              },
              {
                validator: (v, cb) => {
                  if (!v || v.trim().length === 0) {
                    cb(t("form_validation_messages.invalid_password"));
                  } else {
                    cb();
                  }
                },
              },
            ]}
          >
            <Input.Password placeholder={t("login_page.password_placeholder")} />
          </FormItem>

          <div className="auth-forgot">
            <NavLink to={routes.RESETPASSWORD}>
              {t("login_page.forgot_password")}
            </NavLink>
          </div>

          <Button
            htmlType="submit"
            className="auth-primary-btn"
            size="large"
          >
            {t("login_page.log_in_button")}
          </Button>
        </Form>

        <AuthSsoRow mode="login" />
      </AuthFormSection>

      <div className="auth-signup-footer">
        {t("login_page.no_account_message")}{" "}
        <NavLink to={registerHref}>{t("login_page.sign_up_link")}</NavLink>
      </div>
    </AuthSplitLayout>
  );
};
